import { useEffect, useMemo, useState, useCallback } from "react";
import { getPricing } from "../api/settings.js";

export const usePricing = () => {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    getPricing().then((res) => {
      // Use actual Stripe pricing data for CRM
      if (res.addons?.crm?.tiers) {
        // Convert Stripe flat_amount to the expected format
        res.addons.crm.tiers = res.addons.crm.tiers.map((tier) => ({
          up_to: tier.up_to,
          unit_amount: tier.flat_amount,
          flat_amount: tier.flat_amount,
        }));
      }
      setPricing(res);
    });
  }, []);

  const highestBasicPrice = useMemo(() => {
    if (!pricing) return "-";
    return pricing?.basic?.tiers.reduce((acc, tier) => {
      if (tier.unit_amount > acc) {
        return tier.unit_amount;
      }
      return acc;
    }, 0);
  }, [pricing]);

  const highestGrowthPrice = useMemo(() => {
    if (!pricing) return "-";
    return pricing?.growth?.tiers.reduce((acc, tier) => {
      if (tier.unit_amount > acc) {
        return tier.unit_amount;
      }
      return acc;
    }, 0);
  }, [pricing]);

  const addonPrice = useCallback(
    (name) => {
      if (!pricing) {
        return "-";
      }
      return (
        pricing?.addons?.[name]?.tiers ||
        pricing?.addons?.[name]?.unit_amount_decimal ||
        "Coming Soon"
      );
    },
    [pricing]
  );

  const getTiers = useMemo(() => {
    if (!pricing) return {};
    const render = (plan) => {
      let last = 0;
      return pricing[plan].tiers.map((tier) => {
        let range;
        if (last === 0) {
          range = `Up to ${tier.up_to}`;
        } else if (tier.up_to === null) {
          range = `${last} or more`;
        } else {
          range = `${last} - ${tier.up_to}`;
        }
        last = tier.up_to + 1;
        return {
          range,
          price: tier.unit_amount / 100,
          up_to: tier.up_to,
        };
      });
    };
    return { basic: render("basic"), growth: render("growth") };
  }, [pricing]);

  const calculatePrice = useCallback((plan, storeCount) => {
    if (!pricing || !plan) return null;
    const planData = pricing[plan];
    if (!planData?.tiers) return null;
    
    let myTier = planData.tiers.find((tier) => tier.up_to >= storeCount);
    if (!myTier) {
      myTier = planData.tiers[planData.tiers.length - 1];
    }
    return (myTier.unit_amount * storeCount) / 100;
  }, [pricing]);

  return {
    pricing,
    highestBasicPrice,
    highestGrowthPrice,
    addonPrice,
    tiers: getTiers,
    calculatePrice,
  };
};

